import React from "react";
import { Controller, useFormState } from "react-hook-form";
import styled from "styled-components";
import colors from "../../common/colors";
import { getValidationResultFromFormError } from "../../common/validations";
import { fontSize, space } from "../../common/variables";
import Checkbox from "../../components/Checkbox";
import FormDevTool from "../../components/FormDevTool";
import { FormField, FormFieldSet } from "../../components/FormField";
import { CellBlockFooter } from "../../components/Grid/CellBlock";
import TextInput from "../../components/TextInput";
import {
    AdministratorInfoFormData,
    useAdministratorInfoForm,
} from "./AdministratorDetailsView.hooks";
import {
    ADMIN_ROLE_DESCRIPTION,
    ORDERER_ADMIN_ROLE_DESCRIPTION,
    ORDERER_ROLE_DESCRIPTION,
} from "./administratorDetailsConfig";
import { AdministratorFormRole } from "./administratorTypes";

export interface AdministratorInfoData {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    customerCreationAllowed: boolean;
    municipalityRole: AdministratorFormRole;
}

interface Props extends ReturnType<typeof useAdministratorInfoForm> {
    onCancel: () => void;
    onSubmit: (data: AdministratorInfoFormData) => void;
    isNewForm?: boolean;
    isSubmitLoading?: boolean;
    showRoleSelect?: boolean;
    showDemoteRoleSelect?: boolean;
    showCustomerCreationAllowed?: boolean;
    disableSubmit?: boolean;
}

const AdministratorInfoForm = (props: Props) => {
    const { isValid, isDirty } = useFormState({ control: props.control });

    return (
        <form onSubmit={props.handleSubmit(props.onSubmit)}>
            <FormFieldSet>
                <FormField $halfSize>
                    <Controller
                        name="firstName"
                        control={props.control}
                        rules={{ required: true }}
                        render={({ field: { onBlur, onChange, value }, fieldState }) => (
                            <TextInput
                                onBlur={onBlur}
                                onChange={onChange}
                                value={value}
                                required
                                autoComplete="on"
                                placeholder="Förnamn"
                                validation={getValidationResultFromFormError(fieldState)}
                            />
                        )}
                    />
                </FormField>
                <FormField $halfSize>
                    <Controller
                        name="lastName"
                        control={props.control}
                        rules={{ required: true }}
                        render={({ field: { onBlur, onChange, value }, fieldState }) => (
                            <TextInput
                                onBlur={onBlur}
                                onChange={onChange}
                                value={value}
                                required
                                autoComplete="on"
                                placeholder="Efternamn"
                                validation={getValidationResultFromFormError(fieldState)}
                            />
                        )}
                    />
                </FormField>
            </FormFieldSet>
            <FormFieldSet>
                <FormField $halfSize>
                    <Controller
                        name="phone"
                        control={props.control}
                        rules={{ required: true }}
                        render={({ field: { onBlur, onChange, value }, fieldState }) => (
                            <TextInput
                                onBlur={onBlur}
                                onChange={onChange}
                                value={value}
                                autoComplete="on"
                                required
                                placeholder="Telefon"
                                validation={getValidationResultFromFormError(fieldState)}
                            />
                        )}
                    />
                </FormField>
                <FormField $halfSize>
                    <Controller
                        name="email"
                        control={props.control}
                        rules={{ required: true }}
                        render={({ field: { onBlur, onChange, value }, fieldState }) => (
                            <TextInput
                                onBlur={onBlur}
                                onChange={onChange}
                                value={value}
                                autoComplete="on"
                                required
                                placeholder="E-post"
                                validation={getValidationResultFromFormError(fieldState)}
                            />
                        )}
                    />
                </FormField>
            </FormFieldSet>
            {props.showRoleSelect && (
                <FormField>
                    <Controller
                        name="municipalityRole"
                        control={props.control}
                        render={({ field: { onChange, value }, fieldState }) => (
                            <RoleGroup>
                                <RoleLegend>Roll</RoleLegend>
                                <RoleOption>
                                    <input
                                        type="radio"
                                        name="municipalityRole"
                                        checked={value === "ADMIN"}
                                        onChange={() => onChange("ADMIN")}
                                        aria-describedby="admin-role-description"
                                    />
                                    <RoleOptionText>
                                        <RoleOptionTitle>Administratör</RoleOptionTitle>
                                        <RoleOptionHint id="admin-role-description">
                                            {ADMIN_ROLE_DESCRIPTION}
                                        </RoleOptionHint>
                                    </RoleOptionText>
                                </RoleOption>
                                <RoleOption>
                                    <input
                                        type="radio"
                                        name="municipalityRole"
                                        checked={value === "ORDERER_ADMIN"}
                                        onChange={() => onChange("ORDERER_ADMIN")}
                                        aria-describedby="orderer-admin-role-description"
                                    />
                                    <RoleOptionText>
                                        <RoleOptionTitle>
                                            Beställare administratör
                                        </RoleOptionTitle>
                                        <RoleOptionHint id="orderer-admin-role-description">
                                            {ORDERER_ADMIN_ROLE_DESCRIPTION}
                                        </RoleOptionHint>
                                    </RoleOptionText>
                                </RoleOption>
                                {fieldState.error?.message && (
                                    <RoleError>{fieldState.error.message}</RoleError>
                                )}
                            </RoleGroup>
                        )}
                    />
                </FormField>
            )}
            {props.showDemoteRoleSelect && (
                <FormField>
                    <Controller
                        name="municipalityRole"
                        control={props.control}
                        render={({ field: { onChange, value }, fieldState }) => (
                            <RoleGroup>
                                <RoleLegend>Roll</RoleLegend>
                                <RoleOption>
                                    <input
                                        type="radio"
                                        name="municipalityRole"
                                        checked={value === "ORDERER_ADMIN"}
                                        onChange={() => onChange("ORDERER_ADMIN")}
                                        aria-describedby="demote-orderer-admin-role-description"
                                    />
                                    <RoleOptionText>
                                        <RoleOptionTitle>
                                            Beställare administratör
                                        </RoleOptionTitle>
                                        <RoleOptionHint id="demote-orderer-admin-role-description">
                                            {ORDERER_ADMIN_ROLE_DESCRIPTION}
                                        </RoleOptionHint>
                                    </RoleOptionText>
                                </RoleOption>
                                <RoleOption>
                                    <input
                                        type="radio"
                                        name="municipalityRole"
                                        checked={value === "ORDERER"}
                                        onChange={() => onChange("ORDERER")}
                                        aria-describedby="demote-orderer-role-description"
                                    />
                                    <RoleOptionText>
                                        <RoleOptionTitle>Beställare</RoleOptionTitle>
                                        <RoleOptionHint id="demote-orderer-role-description">
                                            {ORDERER_ROLE_DESCRIPTION}
                                        </RoleOptionHint>
                                    </RoleOptionText>
                                </RoleOption>
                                {fieldState.error?.message && (
                                    <RoleError>{fieldState.error.message}</RoleError>
                                )}
                            </RoleGroup>
                        )}
                    />
                </FormField>
            )}
            {props.showCustomerCreationAllowed && (
                <FormField $noMargin>
                    <Controller
                        name="customerCreationAllowed"
                        control={props.control}
                        render={({ field: { onChange, value } }) => (
                            <Checkbox
                                onChange={onChange}
                                value={value}
                                text="Får lägga till nya slutkunder"
                            />
                        )}
                    />
                </FormField>
            )}
            <FormDevTool control={props.control} />
            <CellBlockFooter
                disableSubmit={props.disableSubmit || !isValid || !isDirty}
                hasChangesText={
                    !props.isNewForm && isDirty
                        ? "Förmuläret har osparade ändringar."
                        : undefined
                }
                marginTop
                isLoading={props.isSubmitLoading}
                onCancel={props.onCancel}
                submitButtonType="submit"
            />
        </form>
    );
};

export default AdministratorInfoForm;

const RoleGroup = styled.fieldset`
    display: flex;
    flex-direction: column;
    gap: ${space.small};
    margin: 0;
    padding: 0;
    border: none;
`;

const RoleLegend = styled.legend`
    padding: 0;
    margin-bottom: ${space.small};
    font-size: ${fontSize.small};
    color: ${colors.black};
`;

const RoleOption = styled.label`
    display: flex;
    align-items: flex-start;
    gap: ${space.small};
    font-size: ${fontSize.small};
    color: ${colors.black};
    cursor: pointer;

    input {
        margin-top: 2px;
    }
`;

const RoleOptionText = styled.span`
    display: flex;
    flex-direction: column;
    gap: ${space.xxSmall};
`;

const RoleOptionTitle = styled.span`
    font-size: ${fontSize.small};
    color: ${colors.black};
`;

const RoleOptionHint = styled.span`
    font-size: ${fontSize.xSmall};
    color: ${colors.grayDarker};
`;

const RoleError = styled.span`
    color: ${colors.red};
    font-size: ${fontSize.small};
`;
